/**
 * The ledger pages -- obligations / usage / effects, one page each.
 *
 * Three, not four: `outbox` was dropped at schema v3 (§7.139 ③) and there is no
 * delivery book any more. Whether a message left the building is the sending
 * plugin's effect row, which is the page next door -- so nothing was lost, it
 * moved. Every state gets a colour and a shape, and `unknown` gets its own
 * hatched badge that never collapses into a dot: an obligation the core froze
 * at startup is a fact to be reconciled, not a gap to be zero-filled.
 *
 * Since §7.143 these three rows are not tables this shell reads. `usage` is not
 * a table at all any more, and `obligations` and `effects` are the core's own
 * private indexes, off the tail. What is drawn here is rebuilt from the L0
 * stream (`workbench/server/derived.py`), which is why the columns below are
 * the fields that rebuild can actually state -- not the columns the tables
 * happen to have.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type LedgerRow } from "../lib/api";
import { formatValue, ledgerLabel } from "../lib/format";
import { formatMoney } from "../lib/money";
import { Clip, EmptyState, ErrorState, LoadingState, Scroller, StateBadge } from "../shell/ui";

export const LEDGER_PAGES = [
  {
    name: "obligations",
    title: "欠条",
    note: "每一条输入都在这里；三个终态，一个都不能悄悄丢。核不知道有没有人说过话，所以跑完就是「已办完」。",
  },
  {
    name: "usage",
    title: "用量",
    note: "预留是上界，结算是真值。一次调用两个事件；只有预留没有结算，就是它出网之后没能记下怎么结束——那一行停在「已预留」，本身就是这句话。",
  },
  {
    name: "effects",
    title: "效果",
    note:
      "prepared → 挂起等人 → dispatching → 终态；放开挂起是「挂起结束」，那次调用改在新的一行上跑。" +
      "每一次外发都在这里，包括每一句回复。「等级」和「谁挂住的」只有被人挂住过的那些才有——" +
      "整列「未知」是说没人被问过，不是数据丢了。",
  },
] as const;

const COLUMNS: Record<string, { key: string; label: string; mono?: boolean; num?: boolean; money?: boolean }[]> = {
  obligations: [
    { key: "id", label: "欠条", mono: true },
    { key: "channel", label: "渠道" },
    { key: "dispatch_mode", label: "派发" },
    { key: "session_id", label: "会话", mono: true },
    { key: "principal_id", label: "身份", mono: true },
    // The message itself, which the obligations table never carried: the row
    // pointed at the L0 event and the event is what this page folds now.
    { key: "text", label: "内容" },
  ],
  usage: [
    { key: "id", label: "用量", mono: true },
    { key: "model", label: "模型", mono: true },
    { key: "prompt_tokens", label: "输入 token", num: true },
    { key: "completion_tokens", label: "输出 token", num: true },
    { key: "cached_tokens", label: "缓存命中", num: true },
    // Each row names its own currency, so the unit is written in the cell: `money` was one
    // of five formats this shell offered that `PanelColumn.format` cannot name,
    // so no plugin could ever have asked for it.
    { key: "cost", label: "成本", num: true, money: true },
  ],
  // What the rebuild can state, and no more.  `category` and `proof_hash` were
  // here until §7.140 deleted both columns -- evidence belongs to whoever held
  // the call, not to the core -- and they went on drawing "未知" on every line
  // afterwards, which reads as missing data rather than as a column that cannot
  // exist.  `level` and `held_by` are only known once somebody held the call
  // (they are on `effect.held` and on no earlier event), so a call nobody
  // consulted anybody about shows neither, correctly.
  effects: [
    { key: "id", label: "效果", mono: true },
    { key: "tool", label: "工具", mono: true },
    { key: "call_id", label: "哪次调用", mono: true },
    { key: "level", label: "等级" },
    { key: "held_by", label: "谁挂住的" },
    { key: "turn_id", label: "哪一轮", mono: true },
  ],
};

/**
 * How many rows one page asks for: the newest, in the state picked above them.
 * The server holds a ledger's whole history only as counts; the rows are read
 * a page at a time, and a state is narrowed there rather than here, so picking
 * one shows its newest rows and not whichever of the newest happened to be in it.
 */
export const LEDGER_PAGE_ROWS = 50;

export function LedgerPage({ name, version }: { name: string; version: number }) {
  const [rows, setRows] = useState<LedgerRow[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState<unknown>(null);
  // The state picked is the picked ledger's: moving to another ledger drops it
  // before that ledger is read, not after.
  const [picked, setPicked] = useState<{ name: string; state: string | null }>({ name, state: null });
  const only = picked.name === name ? picked.state : null;
  const setOnly = (state: string | null) => setPicked({ name, state });

  const load = useCallback(() => {
    api
      .ledger(name, { limit: LEDGER_PAGE_ROWS, state: only })
      .then((body) => {
        setRows(body.rows);
        setCounts(body.counts);
        setError(null);
      })
      .catch(setError);
  }, [name, only]);

  useEffect(() => {
    setRows(null);
    load();
  }, [load, version]);

  const meta = LEDGER_PAGES.find((p) => p.name === name);
  const columns = COLUMNS[name] ?? [{ key: "id", label: "id", mono: true }];
  const visible = rows ?? [];

  return (
    <div className="ledger-page">
      <header className="page-head">
        <h1 className="page-title" tabIndex={-1}>
          {meta?.title ?? ledgerLabel(name)}
        </h1>
        <p className="page-note micro">{meta?.note}</p>
      </header>

      <div className="tallies" role="group" aria-label="按状态筛选">
        {Object.entries(counts)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([state, n]) => (
            <button
              key={state}
              type="button"
              className={`tally${only === state ? " tally-on" : ""}`}
              aria-pressed={only === state}
              onClick={() => setOnly(only === state ? null : state)}
            >
              <StateBadge state={state} />
              <span className="tally-n num">{n}</span>
            </button>
          ))}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : rows === null ? (
        <LoadingState label={`读${meta?.title ?? name}`} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={only ? `没有 ${only} 状态的行` : "这本账还是空的"}
          hint={
            only
              ? "点一下上面那个状态可以取消筛选。"
              : "核每写一行，这里就多一行。现在核还没有写过。"
          }
        />
      ) : (
        <Scroller label={`${meta?.title ?? name}表`}>
          <table className="table">
            <caption className="visually-hidden">
              {meta?.title ?? name}，共 {visible.length} 行
            </caption>
            <thead>
              <tr>
                <th scope="col">状态</th>
                {columns.map((col) => (
                  <th key={col.key} scope="col" className={col.num ? "num align-end" : undefined}>
                    {col.label}
                  </th>
                ))}
                <th scope="col">说明</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row, i) => (
                <tr key={String(row.id ?? i)} data-state={String(row.state ?? "unknown")}>
                  <td>
                    <StateBadge state={row.state} />
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={[col.mono ? "mono" : "", col.num ? "num align-end" : ""]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <Clip
                        text={col.money && typeof row[col.key] === "number"
                          ? formatMoney({ amount: row[col.key] as number, currency: row.currency as string | null })
                          : formatValue(row[col.key], col.num ? "number" : "text")}
                        width={col.num ? "16ch" : "32ch"}
                      />
                    </td>
                  ))}
                  <td className="cell-summary">
                    <Clip text={String(row.detail ?? row.reason ?? row.error ?? "")} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scroller>
      )}
    </div>
  );
}
